import { Badge, Nav, Navbar, NavDropdown } from 'react-bootstrap';
import { FaCog, FaSearch, FaShoppingCart, FaUser } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { LinkContainer } from 'react-router-bootstrap';
import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import logo from '../../assets/logosantysmall.png';
import { useLogoutMutation } from '../../slices/userApiSlice';
import { useGetCategoriesQuery } from '../../slices/categoryApiSlice';
import { useGetBrandsQuery } from '../../slices/brandApiSlice';
import { useGetPublicCompaniesQuery } from '../../slices/companyApiSlice';
import SearchBox from '../ui/SearchBox';
import { logout } from '../../slices/authSlice';
import useIsMobile from '../../utils/useIsMobile';
import FloatingCartIcon from '../checkout/FloatingCartIcon';
import ShippingProgressChip from '../ui/ShippingProgressChip';
import CategoryDropdown from '../product/CategoryDropdown';
import CompanySelector from '../admin/CompanySelector';
import HeaderMobileUserMenu from './HeaderMobileUserMenu';
import { clearActiveCompany } from '../../slices/adminSlice';
import { selectCartItems } from '../../slices/cartSlice';
import HeaderNotificationsDropdown from './HeaderNotificationsDropdown';
import useAdminNotifications from '../../hooks/useAdminNotifications';

const HIDE_HEADER_SCROLL_Y = 180;
const SCROLL_DIRECTION_DELTA = 8;
const HIDE_HEADER_SCROLL_DELTA = 56;
const REVEAL_HEADER_SCROLL_DELTA = 28;
const MODE_SWITCH_COOLDOWN_MS = 140;

const extractKeywordFromPath = (pathname = '') => {
    const match = pathname.match(
        /^\/search\/([^/]+)|^\/admin\/productlist\/search\/([^/]+)|^\/admin\/outofstock\/search\/([^/]+)/
    );
    const encodedKeyword = match?.[1] || match?.[2] || match?.[3] || '';

    if (!encodedKeyword) {
        return '';
    }

    try {
        return decodeURIComponent(encodedKeyword).replace(/\+/g, ' ');
    } catch {
        return encodedKeyword.replace(/\+/g, ' ');
    }
};

const Header = () => {
    const isMobile = useIsMobile();
    const location = useLocation();
    const [searchKeyword, setSearchKeyword] = useState(() => extractKeywordFromPath(location.pathname));
    const [mobileHeaderMode, setMobileHeaderMode] = useState('compact');
    const [isSearchFocused, setIsSearchFocused] = useState(false);
    const [isNotificationsPanelOpen, setIsNotificationsPanelOpen] = useState(false);
    const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
    const lastScrollYRef = useRef(0);
    const scrollDirectionRef = useRef(null);
    const directionTravelRef = useRef(0);
    const lastModeChangeAtRef = useRef(0);
    const currentModeRef = useRef('compact');

    const cartItems = useSelector(selectCartItems);
    const cartItemsCount = cartItems.reduce((sum, item) => sum + item.qty, 0);
    const { userInfo } = useSelector((state) => state.auth);

    const dispatch = useDispatch();
    const [logoutMutation] = useLogoutMutation();
    const { data: categories = [], isLoading: loadingCategories } = useGetCategoriesQuery();
    const { data: brands = [], isLoading: loadingBrands } = useGetBrandsQuery();
    const { data: companies = [], isLoading: loadingCompanies } = useGetPublicCompaniesQuery();
    const {
        notifications,
        unreadCount,
        hideBell,
        isLoading: loadingNotifications,
        isMarkingAsRead,
        markNotificationAsRead,
        markAllAsRead,
        handleDropdownToggle,
    } = useAdminNotifications();

    const isInteractionActive = isSearchFocused || isNotificationsPanelOpen || isUserMenuOpen;
    const isStickyHeader = isMobile;
    const isHiddenHeader = isMobile && mobileHeaderMode === 'hidden';
    const shouldCollapseHeaderSides = isStickyHeader && isSearchFocused;
    const shouldShowCompactSearchTrigger = isStickyHeader && !isSearchFocused;

    useEffect(() => {
        setSearchKeyword(extractKeywordFromPath(location.pathname));
    }, [location.pathname]);

    const activeSearchKeyword = searchKeyword.trim();
    const compactSearchLabel = activeSearchKeyword || 'Buscar productos';

    const resetScrollTracking = (scrollY = 0) => {
        lastScrollYRef.current = scrollY;
        scrollDirectionRef.current = null;
        directionTravelRef.current = 0;
    };

    const commitHeaderMode = (nextMode) => {
        if (currentModeRef.current === nextMode) {
            return;
        }

        currentModeRef.current = nextMode;
        lastModeChangeAtRef.current = Date.now();
        directionTravelRef.current = 0;
        setMobileHeaderMode(nextMode);
    };

    const handleNotificationsToggle = (isOpen) => {
        setIsNotificationsPanelOpen(Boolean(isOpen));
        handleDropdownToggle(isOpen);
    };

    useEffect(() => {
        if (!isMobile) {
            resetScrollTracking(0);
            commitHeaderMode('compact');
            return undefined;
        }

        const getScrollY = () => Math.max(window.scrollY || window.pageYOffset || 0, 0);
        const syncDirection = (scrollDelta) => {
            if (Math.abs(scrollDelta) < SCROLL_DIRECTION_DELTA) {
                return;
            }

            const nextDirection = scrollDelta > 0 ? 'down' : 'up';

            if (scrollDirectionRef.current !== nextDirection) {
                scrollDirectionRef.current = nextDirection;
                directionTravelRef.current = Math.abs(scrollDelta);
                return;
            }

            directionTravelRef.current += Math.abs(scrollDelta);
        };

        const evaluateHeaderMode = (scrollY) => {
            const currentMode = currentModeRef.current;
            const now = Date.now();
            const isCoolingDown = now - lastModeChangeAtRef.current < MODE_SWITCH_COOLDOWN_MS;

            if (scrollY <= 4) {
                return 'compact';
            }

            if (scrollY < HIDE_HEADER_SCROLL_Y) {
                return 'compact';
            }

            if (currentMode === 'hidden') {
                if (scrollDirectionRef.current === 'up' && directionTravelRef.current >= REVEAL_HEADER_SCROLL_DELTA && !isCoolingDown) {
                    return 'compact';
                }

                return 'hidden';
            }

            if (scrollDirectionRef.current === 'down' && directionTravelRef.current >= HIDE_HEADER_SCROLL_DELTA && !isCoolingDown) {
                return 'hidden';
            }

            return 'compact';
        };

        resetScrollTracking(getScrollY());

        if (isInteractionActive) {
            commitHeaderMode('compact');
            return undefined;
        }

        let frameId = null;

        const syncHeaderMode = () => {
            const scrollY = getScrollY();
            const scrollDelta = scrollY - lastScrollYRef.current;

            lastScrollYRef.current = scrollY;
            syncDirection(scrollDelta);
            commitHeaderMode(evaluateHeaderMode(scrollY));
        };

        const handleScroll = () => {
            if (frameId !== null) {
                return;
            }

            frameId = window.requestAnimationFrame(() => {
                frameId = null;
                syncHeaderMode();
            });
        };

        const handleResize = () => {
            resetScrollTracking(getScrollY());
            handleScroll();
        };

        syncHeaderMode();
        window.addEventListener('scroll', handleScroll, { passive: true });
        window.addEventListener('resize', handleResize);

        return () => {
            if (frameId !== null) {
                window.cancelAnimationFrame(frameId);
            }
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleResize);
        };
    }, [isInteractionActive, isMobile]);

    const logoutHandler = async () => {
        try {
            await logoutMutation().unwrap();
            dispatch(logout());
            dispatch(clearActiveCompany());
        } catch (e) {
            console.log(e);
        }
    };

    return (
        <>
            <header className={isStickyHeader ? `header-fixed header-fixed-${mobileHeaderMode}` : ''}>
                <Navbar bg="dark" variant="dark" expand="lg" collapseOnSelect className={isStickyHeader ? 'mobile-navbar-compact' : ''}>
                    <div className={`container-fluid d-flex flex-wrap align-items-center w-100 px-md-5 ${isStickyHeader ? 'justify-content-start py-1 px-2 header-mobile-row-compact' : 'justify-content-between py-2 px-3'}`}>
                        {/* LEFT: Logo + Categorías */}
                        <div className={`col-auto col-md-4 d-flex align-items-center justify-content-start flex-shrink-0 gap-2 gap-md-4 order-0 header-animate ${shouldCollapseHeaderSides ? 'collapsed' : ''}`}>
                            <LinkContainer to="/">
                                <Navbar.Brand className="d-flex align-items-center gap-2 flex-row me-0 me-md-2">
                                    <img
                                        src={logo}
                                        alt="HomeSanty"
                                        className="header-logo"
                                    />
                                    <span
                                        className="d-none d-lg-inline"
                                        style={{
                                            fontSize: '1.2rem',
                                            fontWeight: '600',
                                            color: 'white',
                                        }}
                                    >
                                        HomeSanty
                                    </span>
                                </Navbar.Brand>
                            </LinkContainer>

                            {(!loadingCategories || !loadingBrands || !loadingCompanies)
                                && (categories.length > 0 || brands.length > 0 || companies.length > 0) && (
                                <div className="categories-dropdown flex-shrink-0">
                                    <CategoryDropdown
                                        categories={categories}
                                        brands={brands}
                                        companies={companies}
                                        loading={loadingCategories}
                                        loadingBrands={loadingBrands}
                                        loadingCompanies={loadingCompanies}
                                        compact={isStickyHeader}
                                    />
                                </div>
                            )}
                        </div>

                        {/* RIGHT: Cart, User, Admin (Top Right on Mobile) */}
                        <div className={`col-auto col-md-3 d-flex align-items-center justify-content-end flex-shrink-0 ${isStickyHeader ? 'order-2' : 'order-1'} order-md-2 ms-auto ms-md-0 header-animate ${shouldCollapseHeaderSides ? 'collapsed' : ''}`}>
                            {/* Desktop View: visible on md+ */}
                            <Nav className="d-none d-md-flex flex-row align-items-center gap-3 gap-md-4 flex-nowrap">
                                {/* 🛒 Cart */}
                                <div className="d-flex align-items-center">
                                    <LinkContainer to="/cart">
                                        <Nav.Link className="position-relative p-0">
                                            <FaShoppingCart size={24} />
                                            {cartItems.length > 0 && (
                                                <Badge
                                                    pill
                                                    bg="danger"
                                                    className="position-absolute top-0 start-100 translate-middle"
                                                    style={{ fontSize: '0.75rem' }}
                                                >
                                                    {cartItemsCount}
                                                </Badge>
                                            )}
                                        </Nav.Link>
                                    </LinkContainer>
                                </div>

                                <div className="d-flex align-items-center">
                                    <HeaderNotificationsDropdown
                                        notifications={notifications}
                                        unreadCount={unreadCount}
                                        hideBell={hideBell}
                                        isLoading={loadingNotifications}
                                        isMarkingAsRead={isMarkingAsRead}
                                        isMobile={false}
                                            onToggle={handleNotificationsToggle}
                                        onNotificationClick={markNotificationAsRead}
                                        onMarkAllAsRead={markAllAsRead}
                                    />
                                </div>

                                {/* 👤 User */}
                                <div className="d-flex align-items-center">
                                    {userInfo ? (
                                        <NavDropdown
                                            title={<FaUser size={24} />}
                                            id="username"
                                            align="end"
                                            className="header-nav-dropdown"
                                        >
                                            <LinkContainer to="/profile">
                                                <NavDropdown.Item>Perfil</NavDropdown.Item>
                                            </LinkContainer>
                                            <LinkContainer to="/orders">
                                                <NavDropdown.Item>Mis Pedidos</NavDropdown.Item>
                                            </LinkContainer>
                                            {userInfo?.isKiosk && (
                                                <LinkContainer to="/in-store-price-checker">
                                                    <NavDropdown.Item>Verificador de precios en tienda</NavDropdown.Item>
                                                </LinkContainer>
                                            )}
                                            <NavDropdown.Item onClick={logoutHandler}>
                                                Cerrar Sesión
                                            </NavDropdown.Item>
                                        </NavDropdown>
                                    ) : (
                                        <LinkContainer to="/login">
                                            <Nav.Link className="p-0">
                                                <FaUser size={24} />
                                            </Nav.Link>
                                        </LinkContainer>
                                    )}
                                </div>

                                {/* ⚙️ Admin */}
                                {userInfo?.isAdmin && (
                                    <div className="d-flex align-items-center">
                                        <NavDropdown
                                            title={<FaCog size={24} />}
                                            id="adminmenu"
                                            align="end"
                                            className="header-nav-dropdown"
                                        >
                                            {userInfo.isSuperAdmin && (
                                                <>
                                                    <div className="px-2 py-1">
                                                        <CompanySelector />
                                                    </div>
                                                    <NavDropdown.Divider />
                                                </>
                                            )}
                                            <LinkContainer to="/admin/productlist">
                                                <NavDropdown.Item>Productos</NavDropdown.Item>
                                            </LinkContainer>
                                            <LinkContainer to="/admin/outofstock">
                                                <NavDropdown.Item>Out of Stock</NavDropdown.Item>
                                            </LinkContainer>
                                            <LinkContainer to="/admin/categorylist">
                                                <NavDropdown.Item>Categorías</NavDropdown.Item>
                                            </LinkContainer>
                                            <LinkContainer to="/admin/brandlist">
                                                <NavDropdown.Item>Marcas</NavDropdown.Item>
                                            </LinkContainer>
                                            <LinkContainer to="/admin/userlist">
                                                <NavDropdown.Item>Usuarios</NavDropdown.Item>
                                            </LinkContainer>
                                            <LinkContainer to="/admin/orderlist">
                                                <NavDropdown.Item>Pedidos</NavDropdown.Item>
                                            </LinkContainer>
                                            <LinkContainer to="/admin/sales-report">
                                                <NavDropdown.Item>Reportes de ventas</NavDropdown.Item>
                                            </LinkContainer>
                                            <LinkContainer to="/admin/discountlist">
                                                <NavDropdown.Item>Descuentos</NavDropdown.Item>
                                            </LinkContainer>
                                            <LinkContainer to="/admin/eventlist">
                                                <NavDropdown.Item>Eventos y Promociones</NavDropdown.Item>
                                            </LinkContainer>
                                            {userInfo.isSuperAdmin && (
                                                <>
                                                    <LinkContainer to="/admin/companylist">
                                                        <NavDropdown.Item>Empresas</NavDropdown.Item>
                                                    </LinkContainer>
                                                    <LinkContainer to="/admin/partnerlist">
                                                        <NavDropdown.Item>Solicitudes Partners</NavDropdown.Item>
                                                    </LinkContainer>
                                                </>
                                            )}
                                        </NavDropdown>
                                    </div>
                                )}
                            </Nav>

                            {/* Mobile View: visible on sm and below */}
                            <Nav className={`d-flex flex-row d-md-none align-items-center gap-2 ${isStickyHeader ? 'ms-0' : ''}`}>
                                <HeaderNotificationsDropdown
                                    notifications={notifications}
                                    unreadCount={unreadCount}
                                    hideBell={hideBell}
                                    isLoading={loadingNotifications}
                                    isMarkingAsRead={isMarkingAsRead}
                                    isMobile={true}
                                    onToggle={handleNotificationsToggle}
                                    onNotificationClick={markNotificationAsRead}
                                    onMarkAllAsRead={markAllAsRead}
                                />
                                <HeaderMobileUserMenu
                                    userInfo={userInfo}
                                    cartItemsCount={cartItemsCount}
                                    onLogout={logoutHandler}
                                    onOpenChange={setIsUserMenuOpen}
                                />
                            </Nav>
                        </div>

                        {/* CENTER: SearchBox (Below Logo/User on Mobile) */}
                        <div className={`${isStickyHeader ? 'col' : 'col-12 col-md-5'} ${isStickyHeader && !isSearchFocused ? 'mx-2' : ''} d-flex justify-content-center ${isStickyHeader ? 'order-1' : 'order-2 order-md-1'} px-0 search-container-animate`}>
                            {shouldShowCompactSearchTrigger ? (
                                <button
                                    type="button"
                                    className="header-search-trigger"
                                    onClick={() => setIsSearchFocused(true)}
                                    aria-label={activeSearchKeyword ? `Buscar: ${activeSearchKeyword}` : 'Abrir búsqueda'}
                                    title={activeSearchKeyword || 'Abrir búsqueda'}
                                >
                                    <FaSearch size={14} />
                                    <span className="header-search-trigger__label">{compactSearchLabel}</span>
                                </button>
                            ) : (
                                <div
                                    className={`SearchBoxContainer ${isStickyHeader ? 'searchbox-compact' : ''}`}
                                    style={{ width: '100%' }}
                                >
                                    <SearchBox
                                        autoFocus={isStickyHeader && isSearchFocused}
                                        onFocusChange={setIsSearchFocused}
                                        onKeywordChange={setSearchKeyword}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </Navbar>
            </header>
            {isStickyHeader && <div className={`header-mobile-spacer ${isHiddenHeader ? 'hidden' : 'compact'}`} aria-hidden="true" />} {/* Spacer for fixed header */}
            <FloatingCartIcon />
            <ShippingProgressChip />
        </>
    );
};

export default Header;
